/**
 * App Settings Utilities
 * Helper functions for settings data transformation
 */

import type {
    PageKey,
    TopupTier,
    LoyaltyLevel,
    CategoryIconConfig,
    SponsorBrandConfig,
    AppPageSettingsFormValues,
} from './types';
import { PAGE_DEFINITIONS, cloneDefaultValues, DEFAULT_PAGE_SETTINGS } from './constants';

/**
 * Read file as Data URL (base64)
 */
export function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
                resolve(result);
                return;
            }
            reject(new Error('Unexpected file reader result'));
        };
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/**
 * Map incoming settings from API to form values
 */
export function mapIncomingSettings(
    settings: Record<string, unknown> | undefined
): AppPageSettingsFormValues {
    if (!settings) {
        return cloneDefaultValues();
    }

    const pages = (settings.pages ?? {}) as Record<string, unknown>;
    const mapped: Partial<AppPageSettingsFormValues> = {};

    PAGE_DEFINITIONS.forEach(definition => {
        const defaults = DEFAULT_PAGE_SETTINGS[definition.id];
        const incoming = (pages[definition.id] ?? {}) as Record<string, unknown>;
        const pageResult: Record<string, unknown> = { ...(defaults as unknown as Record<string, unknown>) };

        definition.fields.forEach(field => {
            const incomingValue = incoming[field.name];

            // Handle topup-tiers field type
            if (field.type === 'topup-tiers') {
                const incomingTiers = Array.isArray(incomingValue) ? incomingValue : [];
                const normalizedTiers: TopupTier[] = incomingTiers
                    .map(tier => {
                        if (!tier || typeof tier !== 'object') {
                            return null;
                        }

                        const raw = tier as Record<string, unknown>;
                        const amount = Number(raw.amount ?? raw.topupAmount ?? raw.value);
                        const credit = Number(raw.credit ?? raw.creditAmount ?? raw.total ?? raw.amount);

                        if (!Number.isFinite(amount) || !Number.isFinite(credit) || amount <= 0 || credit <= 0) {
                            return null;
                        }

                        return {
                            amount: Math.round(amount),
                            credit: Math.round(credit),
                        };
                    })
                    .filter((tier): tier is TopupTier => Boolean(tier));

                pageResult[field.name] = normalizedTiers.length > 0
                    ? normalizedTiers
                    : (defaults as unknown as Record<string, unknown>)[field.name];
                return;
            }

            // Handle loyalty-levels field type
            if (field.type === 'loyalty-levels') {
                const incomingLevels = Array.isArray(incomingValue) ? incomingValue : [];
                const normalizedLevels: LoyaltyLevel[] = incomingLevels
                    .map(level => {
                        if (!level || typeof level !== 'object') return null;
                        const raw = level as Record<string, unknown>;
                        return {
                            level: Number(raw.level) || 0,
                            name: String(raw.name || ''),
                            nameTh: String(raw.nameTh || ''),
                            minPoints: Number(raw.minPoints) || 0,
                            discountPercent: Number(raw.discountPercent) || 0,
                        };
                    })
                    .filter((level): level is LoyaltyLevel => Boolean(level));
                pageResult[field.name] = normalizedLevels.length > 0
                    ? normalizedLevels
                    : (defaults as unknown as Record<string, unknown>)[field.name];
                return;
            }

            // Handle multi-image field type
            if (field.type === 'multi-image') {
                let normalized: string[] = [];

                if (Array.isArray(incomingValue)) {
                    normalized = incomingValue.filter(
                        (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
                    );
                } else if (typeof incomingValue === 'string') {
                    normalized = incomingValue
                        .split(/\r?\n/)
                        .map(value => value.trim())
                        .filter(Boolean);
                } else if (Array.isArray((defaults as unknown as Record<string, unknown>)[field.name])) {
                    normalized = [...((defaults as unknown as Record<string, unknown>)[field.name] as string[])];
                }

                pageResult[field.name] = normalized;
                return;
            }

            // Handle category-icons field type
            if (field.type === 'category-icons') {
                const defaultIcons = (defaults as unknown as Record<string, unknown>)[field.name] as CategoryIconConfig[];
                const incomingIcons = Array.isArray(incomingValue) ? incomingValue : [];

                // Merge: for each default icon, find matching incoming by id and use its image
                const mergedIcons: CategoryIconConfig[] = defaultIcons.map(defaultIcon => {
                    const incomingIcon = incomingIcons.find(
                        (inc: unknown) => inc && typeof inc === 'object' && (inc as Record<string, unknown>).id === defaultIcon.id
                    ) as Record<string, unknown> | undefined;
                    return {
                        id: defaultIcon.id,
                        title: defaultIcon.title,
                        image: (incomingIcon?.image as string) || '',
                    };
                });

                pageResult[field.name] = mergedIcons;
                return;
            }

            // Handle brand-icons field type
            if (field.type === 'brand-icons') {
                const defaultBrands = (defaults as unknown as Record<string, unknown>)[field.name] as SponsorBrandConfig[];
                const incomingBrands = Array.isArray(incomingValue) ? incomingValue : [];

                // Merge: for each brand slot (1-6), find matching incoming by id
                const mergedBrands: SponsorBrandConfig[] = defaultBrands.map(defaultBrand => {
                    const incomingBrand = incomingBrands.find(
                        (inc: unknown) => inc && typeof inc === 'object' && (inc as Record<string, unknown>).id === defaultBrand.id
                    ) as Record<string, unknown> | undefined;
                    return {
                        id: defaultBrand.id,
                        title: (incomingBrand?.title as string) || '',
                        image: (incomingBrand?.image as string) || '',
                    };
                });

                pageResult[field.name] = mergedBrands;
                return;
            }

            // Handle number field type
            if (field.type === 'number') {
                const numericIncoming = typeof incomingValue === 'number'
                    ? incomingValue
                    : Number(incomingValue);
                if (Number.isFinite(numericIncoming)) {
                    pageResult[field.name] = numericIncoming;
                }
                return;
            }

            // Handle empty/null values
            if (incomingValue === undefined || incomingValue === null || incomingValue === '') {
                if (pageResult[field.name] === undefined || pageResult[field.name] === null) {
                    pageResult[field.name] = '';
                }
                return;
            }

            // Default: convert to string
            pageResult[field.name] = typeof incomingValue === 'string'
                ? incomingValue
                : String(incomingValue);
        });

        (mapped as Record<PageKey, AppPageSettingsFormValues[PageKey]>)[definition.id] =
            pageResult as unknown as AppPageSettingsFormValues[typeof definition.id];
    });

    return mapped as AppPageSettingsFormValues;
}

/**
 * Build outgoing settings for API update
 */
export function buildOutgoingSettings(
    baseSettings: Record<string, unknown>,
    pageId: PageKey,
    pageValues: AppPageSettingsFormValues[PageKey]
): Record<string, unknown> {
    const nextSettings = { ...baseSettings };
    const nextPages = { ...((baseSettings.pages ?? {}) as Record<string, unknown>) };
    nextPages[pageId] = pageValues;
    nextSettings.pages = nextPages;
    return nextSettings;
}
